import { useState } from "react";
import {
  useGetConvictionDcaPicksQuery,
  useRunConvictionDcaSelectionMutation,
  useTickConvictionDcaNowMutation,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtTime } from "@/lib/format";
import { toast } from "sonner";
import { StalenessBadge } from "@/components/domain/StalenessBadge";

/**
 * ConvictionDcaCard — deep-research-driven aggressive accumulation picks.
 *
 * Each pick passes the conviction filter: deep_research decision in
 * GO|WAIT(high conf), outperforms BTC, no imminent unlock, growing
 * ecosystem or catalyst tailwind. Accumulates via $25 fills every 4h
 * up to $300 total. OCO re-placed at blended cost after every fill.
 * Hard stop -25% from avg.
 */
export function ConvictionDcaCard() {
  const [tab, setTab] = useState<"accumulating" | "full" | "exited">("accumulating");
  const picks = useGetConvictionDcaPicksQuery(tab, { pollingInterval: 30000 });
  const [runSel, { isLoading: selecting }] = useRunConvictionDcaSelectionMutation();
  const [tickNow, { isLoading: ticking }] = useTickConvictionDcaNowMutation();
  // Most-recent fill across picks — surfaces whether the engine is
  // actually firing on its 4h cadence or has gone quiet.
  const latestFill = (picks.data ?? []).reduce<string | null>(
    (m: string | null, p: any) => {
      const t = p.last_fill_at || p.selected_at;
      return t && (!m || t > m) ? t : m;
    }, null);

  async function onSelection() {
    if (!confirm("Run selection pass? Deep-researches top 10 candidates (~5 min, $$ Gemini cost).")) return;
    try {
      const r: any = await runSel().unwrap();
      if (r.status === "disabled") {
        toast.error("conviction_dca_enabled is OFF — enable in Settings");
        return;
      }
      const added = r?.added?.length ?? 0;
      const rej = r?.rejected?.length ?? 0;
      toast.success(`selection done — ${added} added, ${rej} rejected`);
    } catch (e: any) {
      toast.error(`selection failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  async function onTick() {
    try {
      const r: any = await tickNow().unwrap();
      if (r.status === "disabled") {
        toast.error("conviction_dca_enabled is OFF");
        return;
      }
      const acts = r?.actions ?? [];
      const filled = acts.filter((a: any) => a.action === "filled").length;
      const stopped = acts.filter((a: any) => a.action === "hard_stopped").length;
      toast.success(`tick done — ${filled} filled, ${stopped} hard-stopped, ${acts.length} total actions`);
    } catch (e: any) {
      toast.error(`tick failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span>Conviction DCA</span>
          <StalenessBadge ts={latestFill} warnHours={4} staleHours={12} veryStaleHours={48} label="last fill" />
          <span className="text-xs text-muted font-normal">
            deep-research-driven aggressive picks · $25/fill · 4h interval · $300/pick max · -25% hard stop
          </span>
        </CardTitle>
        <div className="flex items-center gap-1">
          <div className="flex rounded border overflow-hidden">
            {(["accumulating", "full", "exited"] as const).map((s) => (
              <button key={s}
                      className={`text-xs px-2 py-1 ${tab === s
                        ? "bg-accent/20 text-fg font-semibold" : "bg-bg text-muted hover:bg-muted/20"}`}
                      onClick={() => setTab(s)}>
                {s}
              </button>
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={onTick} disabled={ticking}>
            {ticking ? "Ticking…" : "Tick now"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onSelection} disabled={selecting}>
            {selecting ? "Selecting…" : "Run selection"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {picks.isLoading && <Skeleton className="h-24 w-full" />}
        {picks.data && picks.data.length === 0 && (
          <div className="text-sm text-muted">
            {tab === "accumulating" && "No active picks. Click \"Run selection\" to scan top pre-pump candidates."}
            {tab === "full" && "No picks at max accumulation yet."}
            {tab === "exited" && "No exited picks yet."}
          </div>
        )}
        {picks.data && picks.data.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>Symbol</TH>
                <TH>Status</TH>
                <TH>Selected</TH>
                <TH className="text-right">Verdict</TH>
                <TH className="text-right">Fills</TH>
                <TH className="text-right">Spent</TH>
                <TH className="text-right">Avg cost</TH>
                <TH className="text-right">Stop</TH>
                <TH className="text-right">Target</TH>
                {tab === "exited" && <TH className="text-right">Net P&L</TH>}
                {tab === "exited" && <TH>Reason</TH>}
              </TR>
            </THead>
            <TBody>
              {picks.data.map((p: any) => {
                const tone = p.status === "accumulating" ? "info"
                          : p.status === "full" ? "good"
                          : p.exit_reason === "hard_stop" ? "bad"
                          : "muted";
                return (
                  <TR key={p.id}>
                    <TD className="font-mono font-semibold">{p.symbol}</TD>
                    <TD><Badge tone={tone as any}>{p.status}</Badge></TD>
                    <TD className="text-xs whitespace-nowrap">{fmtTime(p.selected_at)}</TD>
                    <TD className="text-right">
                      <Badge tone={
                        p.verdict_decision === "GO" ? "good"
                        : p.verdict_decision === "WAIT" ? "warn"
                        : "muted"
                      }>{p.verdict_decision} {p.verdict_confidence?.toFixed(0)}</Badge>
                    </TD>
                    <TD className="text-right tabular-nums">{p.fills_count ?? 0}</TD>
                    <TD className="text-right tabular-nums">
                      ${(p.total_size_usd ?? 0).toFixed(2)}
                    </TD>
                    <TD className="text-right tabular-nums text-xs">
                      {p.avg_entry_price ? p.avg_entry_price.toPrecision(6) : "—"}
                    </TD>
                    <TD className="text-right tabular-nums text-xs">
                      {p.stop_price ? p.stop_price.toPrecision(6) : "—"}
                    </TD>
                    <TD className="text-right tabular-nums text-xs">
                      {p.target_price ? p.target_price.toPrecision(6) : "—"}
                    </TD>
                    {tab === "exited" && (
                      <TD className={`text-right tabular-nums ${
                        (p.realized_pnl_usd ?? 0) > 0 ? "text-green-500"
                        : (p.realized_pnl_usd ?? 0) < 0 ? "text-red-500"
                        : ""
                      }`}>
                        {p.realized_pnl_usd != null
                          ? `${p.realized_pnl_usd >= 0 ? "+" : ""}$${p.realized_pnl_usd.toFixed(2)}`
                          : "—"}
                      </TD>
                    )}
                    {tab === "exited" && (
                      <TD className="text-xs text-muted">{p.exit_reason ?? "—"}</TD>
                    )}
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
